import { useCallback, useMemo, useRef } from "react";
import { useHistory } from "react-router";
import { useESChatContext } from "../../../../context/ChatContext/ESChatContext";
import useNotifications from "../../../../hooks/useNotifications";
import useOutsideAlerter from "../../../../hooks/useOutsideAlerter";
import useSystemConfiguration from "../../../../hooks/useSystemConfiguration";
import useTodos from "../../../../hooks/useTodos";
import { useAppState } from "../../../../state/appState";
import { useModalState } from "../../../../state/modalState";
import {
  ADD_OPEN_MODAL,
  CALENDAR_PATH,
  NAVBAR_SLIDE_OUTS,
  RESET_TIMESHEET_STATE,
  SERVICE_REQUEST_CREATE_MODAL,
} from "../../../../constants";
import useCurrentUser from "../../../../hooks/useCurrentUser";
import useAppPersistence from "../../../../hooks/persistence/useAppPersistence";
import useIsSignedIn from "../../../../hooks/useIsSignedIn";
import returnList from "./returnList";

const useTopNavBar = ({ isRegistered, toggleSlideOut, slideOut }) => {
  const { clientChat: client, unread } = useESChatContext();
  const { data: currentUser } = useCurrentUser();
  const [, setIsSignedIn] = useIsSignedIn();
  const { getActiveTab } = useAppPersistence();

  const { data: todos } = useTodos();
  const { data: notifications = [] } = useNotifications();
  const history = useHistory();

  const { data: systemConfiguration } = useSystemConfiguration();
  const [, dispatch] = useAppState();
  const [{ modals }, modalDispatch] = useModalState();
  const popupRef = useRef(null);

  // Detect click outside of the Tooltip popup to close
  useOutsideAlerter(popupRef, () => {
    if (slideOut === NAVBAR_SLIDE_OUTS.userMenu) {
      toggleSlideOut(null);
    }
  });

  const todosCount = useMemo(() => todos?.length ?? 0, [todos?.length]);

  const notificationCount = useMemo(() => {
    return notifications.reduce((count, item) => {
      const addAmount = item.read ? 0 : 1;
      return count + addAmount;
    }, 0);
  }, [notifications]);

  const onLogoClick = () => {
    history.push(CALENDAR_PATH);
  };

  const onLogoKeyDown = (event) => {
    if (event.key === "Enter") {
      onLogoClick();
    }
  };

  const showCreateTicketBell = !modals?.find(
    (item) => item.modalType === SERVICE_REQUEST_CREATE_MODAL
  );

  const openTicketRequest = () => {
    modalDispatch({
      type: ADD_OPEN_MODAL,
      ref: { id: "top_navbar_bell_ticket_modal" },
      modalType: SERVICE_REQUEST_CREATE_MODAL,
      modalData: { isQuickAdd: false },
    });
  };

  const closeHeaderMenu = useCallback(() => {
    document.removeEventListener("click", closeHeaderMenu);
  }, []);

  const selectDropdownItem = useCallback(
    (onClick) => () => {
      closeHeaderMenu();
      onClick();
    },
    [closeHeaderMenu]
  );

  const onLogout = useCallback(async () => {
    setIsSignedIn(false);
    /**
     * Reset Modal State
     * - Removes open modals from session
     */
    modalDispatch({ type: "reset" });
    /**
     * Reset Timesheet State
     * - Returns timesheet overview to initial state
     */
    dispatch({ type: RESET_TIMESHEET_STATE });
  }, [dispatch, modalDispatch, setIsSignedIn]);

  const items = useMemo(() => {
    return returnList({
      isRegistered,
      selectDropdownItem,
      getActiveTab,
      onLogout,
      history,
      currentUser,
    });
  }, [
    currentUser,
    getActiveTab,
    history,
    isRegistered,
    onLogout,
    selectDropdownItem,
  ]);

  return {
    onLogoKeyDown,
    openTicketRequest,
    systemConfiguration,
    dispatch,
    todosCount,
    notificationCount,
    unread,
    client,
    popupRef,
    showCreateTicketBell,
    onLogoClick,
    items,
  };
};

export default useTopNavBar;
